// Catálogo de idiomas soportados por la PWA (i18n). Espejo del módulo del
// backend (api/src/i18n/locales.ts); se mantiene aquí sin importar @vinilos/api
// para no acoplar el bundle del frontend al paquete del backend.

import type { Locale, LocaleInfo } from '../types.js';

export type { Locale, LocaleInfo };

/**
 * Catálogo ordenado de idiomas soportados. El orden define la presentación en
 * los selectores. El PRIMERO (`en`) es el idioma principal y de respaldo.
 */
export const SUPPORTED_LOCALES: readonly LocaleInfo[] = [
  { code: 'en', tag: 'en', nativeName: 'English', spanishName: 'Inglés' },
  { code: 'es', tag: 'es', nativeName: 'Español', spanishName: 'Español' },
  { code: 'zh', tag: 'zh-Hans', nativeName: '中文', spanishName: 'Chino' },
  { code: 'hi', tag: 'hi', nativeName: 'हिन्दी', spanishName: 'Hindi' },
  { code: 'pt', tag: 'pt', nativeName: 'Português', spanishName: 'Portugués' },
  { code: 'fr', tag: 'fr', nativeName: 'Français', spanishName: 'Francés' },
  { code: 'ru', tag: 'ru', nativeName: 'Русский', spanishName: 'Ruso' },
  { code: 'bn', tag: 'bn', nativeName: 'বাংলা', spanishName: 'Bengalí' },
] as const;

/** Idioma principal y de respaldo cuando no hay preferencia válida. */
export const DEFAULT_LOCALE: Locale = 'en';

/** Clave de localStorage donde se recuerda el idioma (sin sesión / fallback). */
export const LOCALE_STORAGE_KEY = 'vinilos.locale';

const SUPPORTED_CODES = new Set<string>(SUPPORTED_LOCALES.map((l) => l.code));

/** ¿`value` es uno de los idiomas soportados? */
export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === 'string' && SUPPORTED_CODES.has(value);
}

/** Lista plana de códigos soportados (en orden del catálogo). */
export const SUPPORTED_CODE_LIST: Locale[] = SUPPORTED_LOCALES.map((l) => l.code);

/**
 * Normaliza un valor arbitrario (p. ej. `navigator.language` "es-ES") al código
 * soportado más cercano, o `fallback` si no reconoce ninguno.
 */
export function normalizeLocale(value: unknown, fallback: Locale = DEFAULT_LOCALE): Locale {
  if (typeof value !== 'string' || value.trim() === '') return fallback;
  const primary = value.split(',')[0]!.trim().split(/[-_;]/)[0]!.toLowerCase();
  return isSupportedLocale(primary) ? primary : fallback;
}

/** Detecta el idioma preferido del navegador entre los soportados. */
export function detectBrowserLocale(fallback: Locale = DEFAULT_LOCALE): Locale {
  if (typeof navigator === 'undefined') return fallback;
  const candidates = navigator.languages ?? [navigator.language];
  for (const c of candidates) {
    const norm = normalizeLocale(c, fallback);
    if (isSupportedLocale(c?.split(/[-_;]/)[0]?.toLowerCase())) return norm;
  }
  return fallback;
}

/** Descriptor de un idioma por su código. */
export function localeInfo(code: Locale): LocaleInfo {
  return SUPPORTED_LOCALES.find((l) => l.code === code) ?? SUPPORTED_LOCALES[0]!;
}
